import { Directive, ElementRef, forwardRef, HostListener, Renderer2 } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[sdPhoneMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneMaskDirective),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneMaskDirective),
      multi: true,
    },
  ],
})
export class PhoneMaskDirective implements ControlValueAccessor {
  private previousValue = '';
  private previousCaretPosition = 0;
  private lastFormattedValue = '+7 ';

  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
  ) {}

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart || 0;

    this.previousValue = input.value;
    this.previousCaretPosition = selectionStart;

    const inputEvent = event as InputEvent;
    const isBackspace = inputEvent.inputType === 'deleteContentBackward';
    const isAutofill =
      inputEvent.inputType === 'insertReplacementText' || inputEvent.inputType === 'insertFromSuggestion';

    const value = this.extractPhoneDigits(input.value);
    const previousDigits = this.extractPhoneDigits(this.lastFormattedValue);
    const isLikelyAutofill = !isBackspace && value.length >= 10 && previousDigits.length <= 1;

    this.applyPhoneValue(input.value, !isAutofill && !isLikelyAutofill, { isBackspace });
  }

  @HostListener('blur')
  onBlur(): void {
    this.applyPhoneValue(this.elementRef.nativeElement.value, false);
    this.onTouched();
  }

  // На старых iOS автозаполнение может сработать только через change
  @HostListener('change', ['$event'])
  onChangeEvent(event: Event): void {
    this.applyPhoneValue((event.target as HTMLInputElement).value, false);
  }

  @HostListener('click', ['$event'])
  @HostListener('focus', ['$event'])
  onClick(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart || 0;
    const value = input.value;

    if (selectionStart < 3) {
      setTimeout(() => {
        input.setSelectionRange(value.length, value.length);
      });
    }

    // iOS autofill может подставить номер в поле с задержкой после focus
    this.scheduleAutofillCheck();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;
    const key = event.key || '';

    this.previousValue = value;
    this.previousCaretPosition = selectionStart || 0;

    if (
      (event.ctrlKey && key === 'a') ||
      (key.startsWith('Arrow') && event.shiftKey) ||
      (key.startsWith('F') && key.length > 1) ||
      ['Tab', 'Escape', 'Enter'].includes(key)
    ) {
      return;
    }

    if (
      !event.ctrlKey &&
      !event.metaKey &&
      key.length === 1 &&
      !/\d/.test(key) &&
      !['Backspace', 'Delete'].includes(key)
    ) {
      event.preventDefault();

      return;
    }

    // Номер полный — не даём вводить ещё цифры (slice(-10) иначе сдвигает номер)
    const hasSelection = selectionStart !== null && selectionEnd !== null && selectionStart !== selectionEnd;
    if (/\d/.test(key) && !hasSelection && this.extractPhoneDigits(value).length >= 10) {
      event.preventDefault();

      return;
    }

    if (
      selectionStart !== null &&
      selectionEnd !== null &&
      selectionStart < 3 &&
      selectionEnd > 0 &&
      !(selectionStart === 0 && selectionEnd === value.length)
    ) {
      event.preventDefault();
      input.setSelectionRange(3, 3);

      return;
    }

    if (selectionStart !== null && selectionStart < 3 && !(selectionStart === 0 && selectionEnd === value.length)) {
      event.preventDefault();
      input.setSelectionRange(3, 3);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData?.getData('text') || '';

    if (!pastedText) return;

    this.applyPhoneValue(pastedText, false);
  }

  writeValue(value: any): void {
    this.applyPhoneValue(value ? value.toString() : '', false, { emitChange: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  validate(control: FormControl): any {
    if (!control.value) {
      return null;
    }

    const value = control.value.toString();

    if (value.length !== 11) {
      return { invalidPhone: true };
    }

    if (!/^7\d{10}$/.test(value)) {
      return { invalidPhone: true };
    }

    return null;
  }

  private applyPhoneValue(
    rawValue: string,
    preserveCaret: boolean,
    options: { isBackspace?: boolean; emitChange?: boolean } = {},
  ): void {
    const { isBackspace = false, emitChange = true } = options;
    const element = this.elementRef.nativeElement;
    const cleanValue = this.extractPhoneDigits(rawValue);
    const formattedValue = this.formatPhoneNumber(cleanValue);
    const modelValue = cleanValue ? '7' + cleanValue : '';

    if (element.value !== formattedValue) {
      this.setInputValue(formattedValue, preserveCaret, isBackspace);
    }

    this.lastFormattedValue = formattedValue;

    if (emitChange) {
      this.onChange(modelValue);
    }
  }

  private scheduleAutofillCheck(): void {
    // на ios автоподстановка может произойти с задержкой поэтому проверяем инпут через 0,100 и 300 мс
    [0, 100, 300].forEach((delay) => {
      setTimeout(() => {
        const element = this.elementRef.nativeElement;

        if (element.value !== this.lastFormattedValue) {
          this.applyPhoneValue(element.value, false);
        }
      }, delay);
    });
  }

  private extractPhoneDigits(rawValue: string): string {
    let value = rawValue.replace(/\D/g, '');

    // Убираем ведущую 7 из фиксированного префикса "+7"
    if (value.startsWith('7')) {
      value = value.substring(1);
    }

    // Автозаполнение на телефоне может вставить 8 сразу после +7
    if (value.startsWith('8')) {
      value = value.substring(1);
    }

    // Для вставки/автозаполнения с лишним кодом страны берём последние 10 цифр
    if (value.length > 10) {
      value = value.slice(-10);
    }

    return value;
  }

  private setInputValue(value: string, preserveCaret: boolean, isBackspace = false): void {
    const element = this.elementRef.nativeElement;
    this.renderer.setProperty(element, 'value', value);

    if (preserveCaret) {
      if (isBackspace) {
        const charBeforeCursor = value.charAt(this.previousCaretPosition);

        if (charBeforeCursor === ' ' && this.previousCaretPosition > 4) {
          this.previousCaretPosition = this.previousCaretPosition - 1;
        }
      }

      setTimeout(() => {
        if (element && typeof element.setSelectionRange === 'function') {
          const newCaretPosition = this.calculateNewCaretPosition(
            this.previousValue,
            value,
            this.previousCaretPosition,
          );
          element.setSelectionRange(newCaretPosition, newCaretPosition);
        }
      });
    } else {
      setTimeout(() => {
        if (element && typeof element.setSelectionRange === 'function') {
          element.setSelectionRange(value.length, value.length);
        }
      });
    }
  }

  private calculateNewCaretPosition(oldValue: string, newValue: string, oldCaretPosition: number): number {
    if (newValue.length < oldValue.length) {
      if (
        oldCaretPosition > 0 &&
        (oldValue[oldCaretPosition - 1] === ' ' || oldValue[oldCaretPosition - 1] === '-') &&
        newValue[oldCaretPosition - 1] === ' '
      ) {
        return Math.max(3, oldCaretPosition - 1);
      }

      return Math.max(3, oldCaretPosition);
    }

    if (newValue.length > oldValue.length) {
      if (
        oldCaretPosition < oldValue.length &&
        (oldValue[oldCaretPosition] === ' ' || oldValue[oldCaretPosition] === '-') &&
        newValue[oldCaretPosition + 1] === ' '
      ) {
        return Math.min(newValue.length, oldCaretPosition + 2);
      }

      return Math.min(newValue.length, oldCaretPosition + 1);
    }

    return Math.max(3, Math.min(newValue.length, oldCaretPosition));
  }

  private formatPhoneNumber(value: string): string {
    if (!value) return '+7 ';

    let formatted = '+7 ';

    if (value.length > 0) {
      formatted += value.substring(0, 3);
    }
    if (value.length > 3) {
      formatted += ' ' + value.substring(3, 6);
    }
    if (value.length > 6) {
      formatted += ' ' + value.substring(6, 8);
    }
    if (value.length > 8) {
      formatted += ' ' + value.substring(8, 10);
    }

    return formatted;
  }

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
}
